type PlanGateCopyInput = {
  /** Nome da página, como no menu ("Disparos"). */
  label: string;
  /** Admin da empresa: quem pode fazer o upgrade. */
  isAdmin: boolean;
  /** Plano atual pelo nome de exibição; `null` quando não dá para nomear. */
  planNow: string | null;
  /** Plano mínimo com o recurso; `null` no fail-closed de oferta. */
  planRequired: string | null;
  /**
   * O primário do admin vai para o comercial (plano legado, conta sem
   * assinatura no Stripe, price divergente): o corpo diz isso, em vez de
   * "Faça o upgrade". Não muda nada para quem não é admin.
   */
  salesPath?: boolean;
};

type PlanGateCopy = {
  eyebrow: string;
  headline: string;
  body: string;
  /** Título da caixa de benefícios; `null` quando não há plano a nomear. */
  unlocksTitle: string | null;
};

/**
 * "Disponível no plano {Lite}", ou "Disponível em outro plano" sem plano
 * mínimo a nomear (fail-closed de oferta). É o eyebrow do bloqueio, o tooltip
 * do item travado no menu e o aviso compacto das páginas que levam ao recurso.
 */
export function planAvailabilityLabel(planRequired: string | null): string {
  return planRequired
    ? `Disponível no plano ${planRequired}`
    : "Disponível em outro plano";
}

/** "{Recurso} não está no plano {Mini}" (ou "no plano atual", sem nome). */
export function planLockHeadline(label: string, planNow: string | null) {
  return `${label} não está no plano ${planNow ?? "atual"}`;
}

/**
 * Copy do bloqueio por plano, verbatim do `gateVals()` do protótipo
 * (docs/design-system/Atendimento.dc.html). Sem plano mínimo conhecido
 * (catálogo falhou ou nenhum plano vendável tem o recurso) as frases perdem o
 * "{Lite}" em vez de nomear um plano inventado; sem o nome do plano atual,
 * falam do "plano atual" sem nomeá-lo. No caminho do comercial (`salesPath`),
 * o corpo do admin troca o "Faça o upgrade" por "Fale com o nosso time
 * comercial", para dizer o que o botão faz (skill billing §7).
 */
export function buildPlanGateCopy({
  label,
  isAdmin,
  planNow,
  planRequired,
  salesPath = false,
}: PlanGateCopyInput): PlanGateCopy {
  const upgradeTo = planRequired ? ` para o ${planRequired}` : "";

  let body: string;
  if (isAdmin && salesPath) {
    const current = planNow ? `O plano atual da conta é o ${planNow}. ` : "";
    const action = planRequired
      ? `fazer o upgrade para o ${planRequired} e liberar`
      : "liberar";
    body = `${current}Fale com o nosso time comercial para ${action} este recurso para toda a equipe.`;
  } else if (isAdmin) {
    const current = planNow ? `O plano atual da conta é o ${planNow}. ` : "";
    body = `${current}Faça o upgrade${upgradeTo} e libere este recurso para toda a equipe.`;
  } else {
    const current = planNow
      ? `O plano atual da conta é o ${planNow} e não inclui este recurso.`
      : "O plano atual da conta não inclui este recurso.";
    body = `${current} Fale com o administrador para avaliar o upgrade${upgradeTo}.`;
  }

  return {
    eyebrow: planAvailabilityLabel(planRequired),
    headline: planLockHeadline(label, planNow),
    body,
    unlocksTitle: planRequired ? `No plano ${planRequired} você libera` : null,
  };
}
